import React from 'react'

import HeadNext from 'next/head'

import getConfig from 'config/public'
import { teal, grey } from 'services/theme/colors'

import { withSelfReferencingHreflang } from './hreflangs'

const EXTRA_METAS_EXCLUDED = ['robots']

/** @type {import('./types').default} */
const Head = ({ metaData, hreflangs }) => {
  const { link_rel: linkRel = [], meta = {}, data_layer = [], title } = metaData
  const { description = '', og = {} } = meta
  const canonicalLink = linkRel?.find(item => item.rel === 'canonical')
  const canonicalUrl = canonicalLink?.url || ''
  const resolvedHreflangs = withSelfReferencingHreflang(hreflangs, canonicalUrl)
  const seoRoutes = ['alternatives', 'compare', 'pricing', 'reviews_listing']
  const currentYear = new Date().getFullYear()
  const routeName = data_layer[0]?.routeName
  const getappTitle = routeName && seoRoutes.includes(routeName) ? ` | GetApp ${currentYear}` : ' | GetApp'
  const isProdEnv = getConfig().env === 'prod'

  const isIndexing = !meta?.robots?.includes('noindex')
  return (
    <HeadNext>
      {/* General */}
      {title && <title>{`${title}${title.startsWith('GetApp |') ? '' : getappTitle}`}</title>}
      <meta name="description" content={description} />
      <meta itemProp="image" content={og.og_logo} />
      {/* Icons */}
      <link href="/apple-touch-icon.png" rel="apple-touch-icon" sizes="180x180" />
      <link href="/favicon.ico" rel="icon" sizes="any" type="image/x-icon" />
      <link href="/safari-pinned-tab.svg" rel="mask-icon" color="#5bbad5" />
      {/* Static */}
      <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=5  shrink-to-fit=no"
      />
      <meta name="format-detection" content="telephone=no, email=no" />
      <meta name="apple-mobile-web-app-capable" content="no" />
      <meta name="referrer" content="unsafe-url" />
      <meta httpEquiv="Accept-CH" content="DPR, Viewport-Width, Width" />
      <meta name="theme-color" content={grey[300]} />
      <meta name="msapplication-TileColor" content={teal[250]} />
      {/* OG */}
      <meta property="og:site_name" content="GetApp" />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={og.og_logo} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={og.og_title} />
      <meta property="og:description" content={og.og_desc} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={og.og_title} />
      <meta name="twitter:image" content={og.og_logo} />
      <meta name="twitter:site" content={og.twitter_site_content} />
      <meta name="twitter:description" content={og.og_desc} />
      {/* Robots */}
      {meta?.robots && <meta name="robots" content={meta.robots} />}

      {/* Other */}
      {(og.extra_metas || [])
        .filter(extra => extra.content && !EXTRA_METAS_EXCLUDED.includes(extra.name))
        .map(extra => (
          <meta key={extra.name} content={extra.content} name={extra.name} />
        ))}
      {/* Links */}

      {isProdEnv && <link href="/site.webmanifest" rel="manifest" />}
      {linkRel && linkRel.map(link => <link href={link.url} rel={link.rel} key={`${link.url}-${link.rel}`} />)}

      {isIndexing &&
        resolvedHreflangs &&
        resolvedHreflangs.map(hrefItem => (
          <link href={hrefItem.link} hrefLang={hrefItem.locale} key={hrefItem.locale} rel="alternate" />
        ))}
    </HeadNext>
  )
}

export default Head
